import { useEffect, useMemo, useState } from "react";
import { apiGet, type Patient, type Provider } from "../api/client";
import { EmptyMessage } from "../components/EmptyMessage";
import { FilterDropdown } from "../components/FilterDropdown";
import { PageHeader, PageHeaderAction } from "../components/PageHeader";
import { PatientsTable } from "../components/PatientsTable";

export function PatientsPage() {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [nameQ, setNameQ] = useState("");
  const [dobQ, setDobQ] = useState("");
  const [emailQ, setEmailQ] = useState("");
  const [showOnly, setShowOnly] = useState("all");
  const [providerId, setProviderId] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiGet<Array<{ patients: Patient[] }>>("/patients"),
      apiGet<Array<{ providers?: Provider[]; doctors?: Provider[] }>>(
        "/doctors",
      ).catch(() => null),
    ])
      .then(([patientData, doctorData]) => {
        if (cancelled) return;
        setPatients(patientData?.[0]?.patients ?? []);
        const row = Array.isArray(doctorData) ? doctorData[0] : doctorData;
        const list = row?.providers || row?.doctors || [];
        setProviders(Array.isArray(list) ? list : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    return patients.filter((p) => {
      if (nameQ.trim()) {
        const term = nameQ.trim().toLowerCase();
        if (!p.name.toLowerCase().includes(term)) return false;
      }
      if (dobQ.trim()) {
        const term = dobQ.trim().toLowerCase();
        const dob = (p.bio?.date_of_birth || p.birthday || "").toLowerCase();
        if (!dob.includes(term)) return false;
      }
      if (emailQ.trim()) {
        const term = emailQ.trim().toLowerCase();
        if (!(p.email || "").toLowerCase().includes(term)) return false;
      }
      if (showOnly === "inactive" && p.account_activated !== false) return false;
      if (showOnly === "active" && p.account_activated === false) return false;
      return true;
    });
  }, [patients, nameQ, dobQ, emailQ, showOnly, providerId]);

  const showOnlyOptions = [
    { key: "all", title: "Show only" },
    { key: "active", title: "Activated patients" },
    { key: "inactive", title: "Not activated" },
  ];

  const providerOptions = useMemo(
    () => [
      { key: "all", title: "Providers" },
      ...providers.map((p) => ({ key: p.id, title: p.name })),
    ],
    [providers],
  );

  return (
    <>
      <PageHeader
        title="Patients"
        actions={
          <PageHeaderAction
            title="Invite a patient"
            icon="ne__icon__add"
            to="/app/add-patient"
          />
        }
      />

      <div className="ne__patients">
        <div className="ne__patients__filters">
          <div className="ne__patients__search">
            <fieldset className="ne__search ne__search--exposed">
              <label>
                <i className="ne__icon__search" />
                <input
                  className="ne__search__input"
                  type="text"
                  placeholder="Name"
                  value={nameQ}
                  onChange={(e) => setNameQ(e.target.value)}
                  aria-label="Name"
                />
              </label>
            </fieldset>
            <fieldset className="ne__search ne__search--exposed">
              <label>
                <i className="ne__icon__search" />
                <input
                  className="ne__search__input"
                  type="text"
                  placeholder="Date of birth"
                  value={dobQ}
                  onChange={(e) => setDobQ(e.target.value)}
                  aria-label="Date of birth"
                />
              </label>
            </fieldset>
            <fieldset className="ne__search ne__search--exposed">
              <label>
                <i className="ne__icon__search" />
                <input
                  className="ne__search__input"
                  type="text"
                  placeholder="Email"
                  value={emailQ}
                  onChange={(e) => setEmailQ(e.target.value)}
                  aria-label="Email"
                />
              </label>
            </fieldset>
          </div>
          <div className="ne__patients__dropdown-show-only">
            <FilterDropdown
              options={showOnlyOptions}
              value={showOnly}
              onChange={setShowOnly}
            />
          </div>
          <div className="ne__patients__dropdown-providers">
            <FilterDropdown
              options={providerOptions}
              value={providerId}
              onChange={setProviderId}
            />
          </div>
        </div>

        {loading ? (
          <EmptyMessage>
            <p>Loading patients…</p>
          </EmptyMessage>
        ) : null}

        {!loading && filtered.length === 0 ? (
          <EmptyMessage title="Sorry, no matching patients!" />
        ) : null}

        {!loading && filtered.length > 0 ? (
          <PatientsTable patients={filtered} />
        ) : null}
      </div>
    </>
  );
}
